// OnboardingWizardPage — v2.5.146
// ─────────────────────────────────────────────────────────────────
// Shown automatically to new users on first dashboard load (only
// when they have NO teams, NO leagues, and NO pending invites).
// The wizard records their choice (single team / multiple teams /
// league) and routes them to the right next-step UI. It does NOT
// create teams or leagues itself — that's left to the existing
// flows so we don't fork the team-creation logic.
//
// Flow:
//   Step 0 (Welcome) → Step 1 (Pick path) → Step 2 (Confirmation)
//
// On final confirm we POST /api/me/onboarding with the chosen value,
// which sets onboarding_completed_at=NOW() server-side. The route
// then navigates to:
//   single_team → /dashboard?openCreateTeam=1 (Dashboard already
//                 supports this deep-link to auto-open the create
//                 wizard the page provides; see Dashboard line 701-ish)
//   multi_team  → /dashboard?openCreateTeam=1 (same — they create
//                 their first team and the dashboard subsequently
//                 hints at adding more)
//   league      → /leagues (the league list page; existing flow
//                 there has a "Create league" button)
//
// Skip handling:
//   The user can click "Skip — I'm just here to join a team" at any
//   step. That POSTS single_team as a safe default (marks them
//   complete so they don't see the wizard again) and navigates to
//   /dashboard. We DON'T fully skip-without-marking because the
//   shouldShow predicate would just bring them back next session.

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Users, Layers, Trophy, ArrowRight, ArrowLeft, Check } from 'lucide-react';
import api from '../services/api.js';
import toast from 'react-hot-toast';
import { resetOnboardingCheck, getCachedOnboardingShouldShow, fetchOnboardingState } from '../services/onboardingCache.js';

const PATHS = [
  {
    id: 'single_team',
    icon: Users,
    title: 'Just one or two teams',
    blurb: 'I coach or manage a beer-league / recreational team and want help with schedules, RSVPs, balanced lineups, and payments.',
    examples: 'Beer league · drop-in hockey · pickup soccer · small adult-rec teams',
    accent: '#00d4ff',
  },
  {
    id: 'multi_team',
    icon: Layers,
    title: 'Multiple teams (still no league setup)',
    blurb: "I coach or manage a few teams that aren't part of a formal league I run. I want one place to see all of them at once.",
    examples: 'Coach with 2-4 teams · parent managing siblings · school program',
    accent: '#a78bfa',
  },
  {
    id: 'league',
    icon: Trophy,
    title: "I'm running a league",
    blurb: "I'm a league admin with multiple teams across divisions, standings, schedule generation, player registration, and so on.",
    examples: 'Minor hockey association · adult league · multi-team tournament',
    accent: '#fbbf24',
  },
];

export default function OnboardingWizardPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);          // 0 = welcome, 1 = pick, 2 = confirm
  const [choice, setChoice] = useState(null);   // selected PATHS[i].id
  const [submitting, setSubmitting] = useState(false);

  // Defensive: if the user navigates directly to /welcome but they
  // already have teams / completed the wizard, send them home. We
  // use the cached shouldShow value (populated by PrivateRoute on
  // its own first check) when available, falling back to a fresh
  // fetch only if we got here without going through PrivateRoute
  // (shouldn't happen in normal flow, but cheap insurance).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cached = getCachedOnboardingShouldShow();
      if (cached !== null) {
        if (!cached && !cancelled) navigate('/dashboard', { replace: true });
        return;
      }
      // Cache miss — fall back to fetching.
      try {
        const result = await fetchOnboardingState();
        if (cancelled) return;
        if (!result) navigate('/dashboard', { replace: true });
      } catch {
        // Fall through and let the user proceed — better to show
        // the wizard than to silently strand them on a blank page.
      }
    })();
    return () => { cancelled = true; };
  }, [navigate]);

  const selectedPath = PATHS.find(p => p.id === choice);

  const completeAndRoute = async (choiceId, destination) => {
    setSubmitting(true);
    try {
      await api.post('/me/onboarding', { choice: choiceId });
      // Invalidate the cache so PrivateRoute re-checks (will see
      // completed=true, shouldShow=false) and lets the destination
      // navigation proceed without bouncing back here.
      resetOnboardingCheck();
      navigate(destination, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Failed to save your choice — please try again');
      setSubmitting(false);
    }
  };

  const handleFinish = () => {
    if (!choice) return;
    // Single + multi_team go to dashboard with `?new=1`, the
    // existing deep-link the dashboard already supports — auto-opens
    // its create-team UI. League goes to the leagues list.
    const dest =
      choice === 'league'
        ? '/leagues'
        : '/dashboard?new=1';
    completeAndRoute(choice, dest);
  };

  const handleSkip = () => {
    // Safe default — single_team — and route to dashboard without
    // the auto-open query. They land on the empty dashboard with
    // its normal "Welcome" message + CTA. We MARK complete so this
    // wizard doesn't haunt them next session.
    completeAndRoute('single_team', '/dashboard');
  };

  return (
    <div style={{
      // Mobile/Capacitor fix — plain 100vh doesn't track the visual
      // viewport on mobile browsers (address-bar show/hide) or the
      // Capacitor WebView's keyboard-adjusted height, unlike the
      // Tailwind `min-h-screen` utility which index.css already
      // overrides to 100dvh app-wide. This page uses an inline style
      // instead of that class, so it needs the same var directly.
      minHeight: 'var(--mobile-h, 100dvh)',
      background: 'var(--bg, #060a0f)',
      color: 'var(--text, #f0faff)',
      padding: '40px 20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}>
      <div style={{
        maxWidth: 680,
        width: '100%',
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 18,
        padding: '40px 36px',
      }}>
        {/* Step indicator */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginBottom: 28,
          justifyContent: 'center',
        }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: i === step ? 28 : 8,
              height: 8,
              borderRadius: 999,
              background: i <= step ? 'var(--ice, #00d4ff)' : 'rgba(255,255,255,0.15)',
              transition: 'width 0.2s ease',
            }} />
          ))}
        </div>

        {/* Step 0 — Welcome */}
        {step === 0 && (
          <div style={{ textAlign: 'center' }}>
            <div style={{
              fontSize: 56,
              marginBottom: 16,
              lineHeight: 1,
            }}>🏒</div>
            <h1 style={{
              fontFamily: 'var(--font-display, Raleway, sans-serif)',
              fontSize: 32,
              fontWeight: 800,
              letterSpacing: '0.02em',
              marginBottom: 12,
              color: 'var(--ice, #00d4ff)',
            }}>Welcome to Game Day</h1>
            <p style={{
              fontSize: 17,
              lineHeight: 1.55,
              color: 'var(--text-2, #a0afc4)',
              marginBottom: 32,
              maxWidth: 500,
              margin: '0 auto 32px',
            }}>
              We'll set you up in under a minute. First — tell us a bit about what you're managing so we can tailor the app to fit.
            </p>
            <button
              onClick={() => setStep(1)}
              style={primaryBtn}>
              Get started <ArrowRight size={16} style={{ marginLeft: 6 }} />
            </button>
            <div style={{ marginTop: 18 }}>
              <button
                onClick={handleSkip}
                disabled={submitting}
                style={linkBtn}>
                Skip — I'm just here to join a team
              </button>
            </div>
          </div>
        )}

        {/* Step 1 — Pick a path */}
        {step === 1 && (
          <div>
            <h2 style={stepHeading}>What are you setting up?</h2>
            <p style={stepSubcopy}>
              Pick the option that best fits how you'll use Game Day. You can change this later in Settings.
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 24 }}>
              {PATHS.map(p => {
                const Icon = p.icon;
                const selected = choice === p.id;
                return (
                  <button
                    key={p.id}
                    onClick={() => setChoice(p.id)}
                    style={{
                      ...cardBtn,
                      borderColor: selected ? p.accent : 'rgba(255,255,255,0.1)',
                      background: selected ? `${p.accent}10` : 'rgba(255,255,255,0.02)',
                    }}>
                    <div style={{
                      width: 44, height: 44, borderRadius: 10,
                      background: `${p.accent}1a`,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0,
                    }}>
                      <Icon size={22} style={{ color: p.accent }} />
                    </div>
                    <div style={{ flex: 1, textAlign: 'left' }}>
                      <div style={{
                        fontFamily: 'var(--font-display, Raleway, sans-serif)',
                        fontSize: 16, fontWeight: 700, color: 'var(--text)',
                        marginBottom: 4,
                      }}>{p.title}</div>
                      <div style={{ fontSize: 13.5, color: 'var(--text-2, #a0afc4)', lineHeight: 1.45, marginBottom: 6 }}>
                        {p.blurb}
                      </div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-3, #6a7588)', fontStyle: 'italic' }}>
                        e.g. {p.examples}
                      </div>
                    </div>
                    {selected && (
                      <div style={{
                        width: 24, height: 24, borderRadius: 999,
                        background: p.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        flexShrink: 0,
                      }}>
                        <Check size={15} strokeWidth={3} style={{ color: '#000' }} />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>

            {/* flex-wrap — the 40px+36px page/card padding leaves only
                ~280px of content width at 390px, and Back+Continue
                (with uppercase letter-spacing) can be tight; wrap
                rather than let the Continue button clip/overflow. */}
            <div style={{ display: 'flex', gap: 10, marginTop: 28, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <button onClick={() => setStep(0)} style={secondaryBtn}>
                <ArrowLeft size={15} style={{ marginRight: 6 }} /> Back
              </button>
              <button onClick={() => setStep(2)} disabled={!choice} style={{
                ...primaryBtn,
                opacity: choice ? 1 : 0.4,
                cursor: choice ? 'pointer' : 'not-allowed',
              }}>
                Continue <ArrowRight size={15} style={{ marginLeft: 6 }} />
              </button>
            </div>

            <div style={{ marginTop: 18, textAlign: 'center' }}>
              <button onClick={handleSkip} disabled={submitting} style={linkBtn}>
                Skip — I'm just here to join a team
              </button>
            </div>
          </div>
        )}

        {/* Step 2 — Confirm + go */}
        {step === 2 && selectedPath && (
          <div>
            <h2 style={stepHeading}>You're all set</h2>
            <p style={stepSubcopy}>
              We'll route you to the right place to get started.
            </p>

            <div style={{
              background: `${selectedPath.accent}10`,
              border: `1px solid ${selectedPath.accent}55`,
              borderRadius: 14,
              padding: 24,
              marginTop: 24,
              textAlign: 'center',
            }}>
              <div style={{ marginBottom: 14 }}>
                {(() => { const Icon = selectedPath.icon;
                  return <Icon size={36} style={{ color: selectedPath.accent }} />;
                })()}
              </div>
              <div style={{
                fontFamily: 'var(--font-display, Raleway, sans-serif)',
                fontSize: 18, fontWeight: 700, color: 'var(--text)', marginBottom: 8,
              }}>{selectedPath.title}</div>
              <div style={{ fontSize: 14, color: 'var(--text-2)', lineHeight: 1.5, maxWidth: 460, margin: '0 auto' }}>
                {selectedPath.id === 'league'
                  ? "Next: we'll take you to the league dashboard where you can create your league, set up seasons, and invite teams."
                  : "Next: we'll take you to your team dashboard where you can create your first team."}
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, marginTop: 28, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              <button onClick={() => setStep(1)} disabled={submitting} style={secondaryBtn}>
                <ArrowLeft size={15} style={{ marginRight: 6 }} /> Change my mind
              </button>
              <button onClick={handleFinish} disabled={submitting} style={primaryBtn}>
                {submitting ? 'Setting up…' : (<>Take me there <ArrowRight size={15} style={{ marginLeft: 6 }} /></>)}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── styles ──────────────────────────────────────────────────────
const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '13px 24px',
  background: 'var(--ice, #00d4ff)',
  color: '#000',
  border: 'none',
  borderRadius: 10,
  fontFamily: 'var(--font-display, Raleway, sans-serif)',
  fontWeight: 700,
  fontSize: 14,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const secondaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '13px 22px',
  background: 'transparent',
  color: 'var(--text-2, #a0afc4)',
  border: '1px solid rgba(255,255,255,0.15)',
  borderRadius: 10,
  fontFamily: 'var(--font-display, Raleway, sans-serif)',
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};
const linkBtn = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-3, #6a7588)',
  fontSize: 12.5,
  textDecoration: 'underline',
  cursor: 'pointer',
  padding: 0,
};
const stepHeading = {
  fontFamily: 'var(--font-display, Raleway, sans-serif)',
  fontSize: 26,
  fontWeight: 800,
  letterSpacing: '0.02em',
  marginBottom: 8,
  color: 'var(--text)',
};
const stepSubcopy = {
  fontSize: 15,
  lineHeight: 1.5,
  color: 'var(--text-2, #a0afc4)',
};
const cardBtn = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 14,
  padding: '16px 18px',
  background: 'rgba(255,255,255,0.02)',
  border: '1px solid rgba(255,255,255,0.1)',
  borderRadius: 12,
  cursor: 'pointer',
  textAlign: 'left',
  fontFamily: 'inherit',
  color: 'inherit',
  width: '100%',
  transition: 'border-color 0.15s, background 0.15s',
};
